// SL.L1a-1 (#2957) — the board's sprint selector, header, unlinked-issue
// notice and closed-sprint history (rulings 1, 2, 10 and 11 of 6254753350).
// No actions here: SL.L1a-2's action bar is passed in through `renderActions`.
import { useEffect, useId, useState, type ReactNode } from 'react';
import { apiFetch } from '../../lib/api';
import { ALL_ACTIVE, CLOSED_SPRINTS, type BoardSelection } from '../../lib/boardSprintScope';
import type { Sprint, SprintState } from '../../lib/sprintsApi';

const STATE_LABEL: Record<SprintState, string> = {
  draft: 'Draft', approved: 'Approved', active: 'Active', in_retrospective: 'In retrospective', closed: 'Closed',
};

const stateLabel = (s: Sprint) => STATE_LABEL[s.state] ?? s.state;

export function formatSprintDate(iso: string): string {
  return new Date(iso).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' });
}

// Read by screen readers, not shown.
const srOnly: React.CSSProperties = {
  position: 'absolute', width: 1, height: 1, padding: 0, margin: -1, overflow: 'hidden', clip: 'rect(0, 0, 0, 0)', whiteSpace: 'nowrap', border: 0,
};

const text: React.CSSProperties = { fontFamily: 'var(--font-body)', fontSize: 13, color: 'var(--text-secondary)' };

// ── Selector ─────────────────────────────────────────────────────────────────

export function SprintSelector({ sprints, value, onChange }: {
  sprints: readonly Sprint[]; value: BoardSelection; onChange: (v: BoardSelection) => void;
}) {
  const id = useId();
  return (
    <span style={{ display: 'inline-flex', alignItems: 'baseline', gap: 6 }}>
      <label htmlFor={id} style={text}>Sprint</label>
      <select
        id={id}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        style={{
          fontFamily: 'var(--font-body)', fontSize: 13, padding: '4px 8px', borderRadius: 'var(--radius-sm)',
          border: '1px solid var(--border)', background: 'var(--surface)', color: 'var(--text)', cursor: 'pointer',
        }}
      >
        <option value={ALL_ACTIVE}>All active</option>
        {sprints.map((s) => <option key={s.id} value={s.id}>{`${s.name} (${stateLabel(s).toLowerCase()})`}</option>)}
        <option value={CLOSED_SPRINTS}>Closed sprints</option>
      </select>
    </span>
  );
}

// ── Header ───────────────────────────────────────────────────────────────────

/** Points done: live from `progress` while active, the stored figure once closed, else none. */
function donePointsOf(s: Sprint): number | null {
  if (s.state === 'active') return s.progress?.donePoints ?? null;
  if (s.state === 'closed') return s.donePoints;
  return null;
}

function SprintHeaderLine({ sprint: s, compact, actions }: { sprint: Sprint; compact: boolean; actions?: ReactNode }) {
  const done = donePointsOf(s);
  const dates = [
    s.approvedAt && `Approved ${formatSprintDate(s.approvedAt)}`,
    s.startedAt && `Started ${formatSprintDate(s.startedAt)}`,
    s.closedAt && `Closed ${formatSprintDate(s.closedAt)}`,
  ].filter((d): d is string => !!d);
  const nullEstimates = s.progress?.nullEstimates ?? 0;
  return (
    <div data-testid="sprint-header" style={{ display: 'flex', alignItems: 'baseline', gap: 10, flexWrap: 'wrap', marginTop: compact ? 2 : 6 }}>
      <strong style={{ fontFamily: 'var(--font-body)', fontSize: 14, color: 'var(--text)' }}>{s.name}</strong>
      <span style={{ ...text, fontWeight: 600 }}>{stateLabel(s)}</span>
      {!compact && s.goal && <span style={text}>{`Goal: ${s.goal}`}</span>}
      {dates.map((d) => <span key={d} style={text}>{d}</span>)}
      {done !== null && <span style={{ ...text, color: 'var(--text)' }}>{`${done} of ${s.plannedPoints} points done`}</span>}
      {s.progress && <span style={text}>{`${s.progress.openItems} open`}</span>}
      {nullEstimates > 0 && (
        <span style={{ ...text, color: 'var(--warning)' }}>
          {`${nullEstimates} done ${nullEstimates === 1 ? 'item has' : 'items have'} no estimate`}
        </span>
      )}
      {actions}
    </div>
  );
}

/**
 * One full header for a selected sprint; one compact line per active sprint
 * for All active. Each line carries that sprint's own actions (L1a-2, recon Q6).
 */
export function SprintHeader({ sprints, selection, renderActions }: {
  sprints: readonly Sprint[]; selection: BoardSelection; renderActions?: (s: Sprint) => ReactNode;
}) {
  if (selection === ALL_ACTIVE) {
    const active = sprints.filter((s) => s.state === 'active');
    if (active.length === 0) return <p style={{ ...text, margin: '6px 0 0' }}>No sprint is active.</p>;
    return <div>{active.map((s) => <SprintHeaderLine key={s.id} sprint={s} compact actions={renderActions?.(s)} />)}</div>;
  }
  const one = sprints.find((s) => s.id === selection);
  return one ? <SprintHeaderLine sprint={one} compact={false} actions={renderActions?.(one)} /> : null;
}

// ── Unlinked issues ──────────────────────────────────────────────────────────

export function UnlinkedNotice({ count, selection, onShowAllActive }: {
  count: number; selection: BoardSelection; onShowAllActive: () => void;
}) {
  if (count === 0 || selection === CLOSED_SPRINTS) return null;
  if (selection === ALL_ACTIVE) {
    return (
      <p role="status" style={{ ...text, margin: '0 0 var(--space-4)' }}>
        {count === 1
          ? "1 issue isn't linked to a sprint yet. It appears here until its sprint is approved."
          : `${count} issues aren't linked to a sprint yet. They appear here until their sprint is approved.`}
      </p>
    );
  }
  return (
    <p role="status" style={{ margin: '0 0 var(--space-4)' }}>
      <button
        type="button"
        onClick={onShowAllActive}
        style={{ ...text, color: 'var(--primary)', background: 'none', border: 'none', padding: 0, cursor: 'pointer', textDecoration: 'underline' }}
      >
        {`${count} unlinked ${count === 1 ? 'issue' : 'issues'} not shown`}
      </button>
    </p>
  );
}

// ── Closed-sprint history (D9a) ──────────────────────────────────────────────

interface RosterRow { userId: string; userName: string }

export function ClosedSprintHistory({ projectId, sprints }: { projectId: string; sprints: readonly Sprint[] }) {
  const closed = sprints.filter((s) => s.state === 'closed').sort((a, b) => a.number - b.number);
  const [names, setNames] = useState<ReadonlyMap<string, string>>(new Map());
  const [namesFailed, setNamesFailed] = useState(false);

  // Another project starts with no names and no failure (L1a-2, re-probe
  // 6255525187 P3 2). Adjusted during render, as BoardPage does.
  const [namesFor, setNamesFor] = useState(projectId);
  if (namesFor !== projectId) {
    setNamesFor(projectId);
    setNames(new Map());
    setNamesFailed(false);
  }

  // closedBy is a user id: name it from the project roster. A closer who is
  // not on the roster shows —; a failed read also says the names are
  // unavailable, so the two read differently (fix round 6255091113 item 4).
  // A read for a previous project is ignored.
  useEffect(() => {
    let ignore = false;
    apiFetch<RosterRow[]>(`/projects/${encodeURIComponent(projectId)}/members`)
      .then((rows) => { if (!ignore && Array.isArray(rows)) setNames(new Map(rows.map((r) => [r.userId, r.userName]))); })
      .catch(() => { if (!ignore) setNamesFailed(true); });
    return () => { ignore = true; };
  }, [projectId]);

  if (closed.length === 0) return <p style={text}>No sprint has closed yet.</p>;

  const maxDone = Math.max(1, ...closed.map((s) => s.donePoints));
  const cell: React.CSSProperties = { ...text, color: 'var(--text)', padding: '8px 12px', borderBottom: '1px solid var(--border)', textAlign: 'left' };
  return (
    <div style={{ background: 'var(--surface)', border: '1px solid var(--border)', borderRadius: 'var(--radius-lg)', padding: 'var(--space-6)' }}>
      <h2 style={{ ...text, fontSize: 12, textTransform: 'uppercase', letterSpacing: '0.06em', margin: '0 0 8px' }}>Velocity</h2>
      <ol aria-label="Velocity" style={{ display: 'flex', alignItems: 'flex-end', gap: 8, listStyle: 'none', padding: 0, margin: '0 0 var(--space-5)', height: 64 }}>
        {closed.map((s) => (
          <li key={s.id} title={`${s.name}: ${s.donePoints} points done`}
            style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 2, justifyContent: 'flex-end', height: '100%' }}>
            <span data-sr-only style={srOnly}>{`${s.name}: ${s.donePoints} points done`}</span>
            <span aria-hidden="true" style={{ ...text, fontSize: 11 }}>{s.donePoints}</span>
            <span style={{ width: 18, height: `${Math.round((s.donePoints / maxDone) * 40)}px`, minHeight: 1, background: 'var(--primary)', borderRadius: 2 }} />
          </li>
        ))}
      </ol>
      {/* Outside the table, so the column header's name stays "Closed by" (re-probe P3 3). */}
      {namesFailed && <p style={{ ...text, fontSize: 12, margin: '0 0 6px' }}>{"Names unavailable: the project's members couldn't be read."}</p>}
      <div style={{ overflowX: 'auto' }}>
        <table aria-label="Closed sprints" style={{ borderCollapse: 'collapse', width: '100%' }}>
          <thead>
            <tr>
              {['Sprint', 'Closed', 'Closed by', 'Planned', 'Done', 'Done ÷ planned'].map((h) => (
                <th key={h} scope="col" style={{ ...cell, color: 'var(--text-secondary)', fontWeight: 600 }}>{h}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {closed.map((s) => (
              <tr key={s.id}>
                <td style={cell}>{s.name}</td>
                <td style={cell}>{s.closedAt ? formatSprintDate(s.closedAt) : '—'}</td>
                <td style={cell}>{(s.closedBy && names.get(s.closedBy)) || '—'}</td>
                <td style={cell}>{s.plannedPoints}</td>
                <td style={cell}>{s.donePoints}</td>
                <td style={cell}>{s.plannedPoints > 0 ? `${Math.round((s.donePoints / s.plannedPoints) * 100)}%` : '—'}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
